import { toString as toString_1, Record } from "./fable_modules/fable-library-js.4.24.0/Types.js";
import { record_type, option_type, string_type, int32_type } from "./fable_modules/fable-library-js.4.24.0/Reflection.js";
import { defaultArg, some } from "./fable_modules/fable-library-js.4.24.0/Option.js";
import { parse } from "./fable_modules/fable-library-js.4.24.0/Int32.js";
import { toUniversalTime, toString, parse as parse_1 } from "./fable_modules/fable-library-js.4.24.0/Date.js";
import { isNullOrWhiteSpace, printf, toText, replace } from "./fable_modules/fable-library-js.4.24.0/String.js";
import { sortByDescending, sortBy, setItem, fill } from "./fable_modules/fable-library-js.4.24.0/Array.js";
import { int32ToString, compare, equals } from "./fable_modules/fable-library-js.4.24.0/Util.js";
import { HookImports_useMediaQuery } from "./fable_modules/Feliz.MaterialUI.5.13.7-alpha.8/Hooks.fs.js";
import { RouterModule_encodeParts } from "./fable_modules/Feliz.Router.4.0.0/Router.fs.js";
import { ofArray } from "./fable_modules/fable-library-js.4.24.0/List.js";

export class TextFieldEventData extends Record {
    constructor(value, name) {
        super();
        this.value = (value | 0);
        this.name = name;
    }
}

export function TextFieldEventData_$reflection() {
    return record_type("Utils.TextFieldEventData", [], TextFieldEventData, () => [["value", int32_type], ["name", option_type(string_type)]]);
}

export function tryInt(value) {
    try {
        return parse(value, 511, false, 32);
    }
    catch (e) {
        console.error(some("[Utils] Could not turn string %s into int: %o"), value, e);
        return undefined;
    }
}

export function tryGetById(id) {
    const elem = document.getElementById(id);
    if (elem == null) {
        return undefined;
    }
    else {
        return elem;
    }
}

export function parseDateIso(dateStr) {
    return parse_1(dateStr);
}

/**
 * Try to parse provided ISO-8601 string to datetime object and convert to local (norwegian) presentation
 */
export function fromUtcToLocalString(utcString) {
    try {
        return toString(parse_1(utcString), "dd/MM/yyyy HH:mm");
    }
    catch (e) {
        console.error(some("Utils.fromUtcToLocalString failed parsing ISO-8601 string"), utcString, " with error:", e);
        return utcString;
    }
}

/**
 * try to parse provided ISO-8601 string to datetime object and present as local date
 */
export function fromUtcToLocalDate(utcString) {
    try {
        return toString(parse_1(utcString), "dd/MM/yyyy");
    }
    catch (e) {
        console.error(some("Utils.fromUtcToLocalDate failed parsing ISO-8601 string"), utcString, " with error:", e);
        return utcString;
    }
}

/**
 * Formats a DateTime to "yyyy-MM-dd HH:mmZ"
 */
export function toUtcString(dt) {
    return toString(toUniversalTime(dt), "yyyy-MM-dd HH:mmZ");
}

/**
 * Formats a DateTime to "2024-09-25T11:52:52.000Z" using JS Date object format method
 */
export function toISOString(date) {
    const jsDate = new Date(toString_1(date));
    return jsDate.toISOString();
}

/**
 * Takes a DateTime string "yyyy-MM-ddTHH:mm:ssZ" and ensures its Compliant to Thoth.Json parsing when sent over the wire. So it needs to be in the format "yyyy-MM-dd HH:mmZ", so that Thoth.Json doesn't throw a type error that it found a DateTime type when it was expecting a string
 */
export function canonicolizeDateTimeStr(dateStr) {
    return toUtcString(parseDateIso(dateStr));
}

/**
 * converts month number to shortened string presentation
 */
export function toAbbrMonth(lang, x) {
    switch (x) {
        case 1:
            return "Jan";
        case 2:
            return "Feb";
        case 3:
            return "Mar";
        case 4:
            return "Apr";
        case 5:
            if (lang.tag === 1) {
                return "May";
            }
            else {
                return "Mai";
            }
        case 6:
            return "Jun";
        case 7:
            return "Jul";
        case 8:
            return "Aug";
        case 9:
            return "Sep";
        case 10:
            if (lang.tag === 1) {
                return "Oct";
            }
            else {
                return "Okt";
            }
        case 11:
            return "Nov";
        case 12:
            if (lang.tag === 1) {
                return "Dec";
            }
            else {
                return "Des";
            }
        default:
            return "N/A";
    }
}

/**
 * Formats money-value to space-seperated thousands presentation
 */
export function formatMoney(lang, nok) {
    if (lang.tag === 1) {
        const format_1 = new Intl.NumberFormat("en-GB", {
            currency: "GBP",
            style: "currency",
        });
        const gbp = nok / 13.86;
        return format_1.format(gbp);
    }
    else {
        const format = new Intl.NumberFormat("no-NB", {
            currency: "nok",
            style: "currency",
        });
        return format.format(nok);
    }
}

/**
 * Converts a string to a URL compatible format
 */
export function toUrlSafeString(s) {
    return encodeURIComponent(s);
}

/**
 * Gets the current url of the page, but replaces the /#/ of the url with just /
 */
export function getUrlAsPath() {
    const url = replace(window.location.href, "#/", "");
    return new URL(url);
}

export function setLocationHash(path) {
    window.location.hash = (("#" + path.pathname) + path.search);
}

export function setQuery(key, value) {
    const path = getUrlAsPath();
    path.searchParams.set(key, value);
    setLocationHash(path);
}

export function getQuery(key) {
    const path = getUrlAsPath();
    return path.searchParams.get(key);
}

export function removeQuery(key) {
    const path = getUrlAsPath();
    path.searchParams.delete(key);
    setLocationHash(path);
}

/**
 * Turn JS Filelist to File array
 */
export function getFiles(fileList) {
    const files = fill(new Array(fileList.length), 0, fileList.length, null);
    for (let idx = 0; idx <= (fileList.length - 1); idx++) {
        setItem(files, idx, fileList[idx]);
    }
    return files;
}

export function testDragLeave(elem, ev) {
    const self = elem;
    const nodeLeft = ev.target;
    const nodeEntered = ev.relatedTarget;
    const enteredSelf = equals(self, nodeEntered);
    const enteredChild = !enteredSelf && self.contains(nodeEntered);
    const enteredParent = !enteredSelf && nodeEntered.contains(self);
    if ((equals(self, nodeLeft) && !enteredChild) ? true : enteredParent) {
        return true;
    }
    else if (!enteredChild) {
        return !enteredParent;
    }
    else {
        return false;
    }
}

/**
 * Helper function for debounce. Which is when you want to wait for something, like input, but
 * also be able to cancel the "sleep". A good usecase for this is searching in a very big
 * collection. You do not want to evaluate the searching function on every key stroke, so you
 * debounce every stroke with this timeout. Then, when the user has finally finished writing their
 * word or search term, will the function evaluate.
 */
export function debounce(timeout, timer, f) {
    clearTimeout(timer.current);
    timer.current = setTimeout(f, timeout);
}

export function sortFnWithAccessor(asc, sortKey, accessor) {
    return (asc ? ((projection) => ((array) => sortBy(projection, array, {
        Compare: compare,
    }))) : ((projection_1) => ((array_1) => sortByDescending(projection_1, array_1, {
        Compare: compare,
    }))))((entry) => (accessor(entry)[sortKey]));
}

export function sortFn(asc, sortKey) {
    return sortFnWithAccessor(asc, sortKey, (x) => x);
}

export class Breakpoints extends Record {
    constructor(Mobile, MobileLandscape, Tablet, Desktop, WideScreen) {
        super();
        this.Mobile = (Mobile | 0);
        this.MobileLandscape = (MobileLandscape | 0);
        this.Tablet = (Tablet | 0);
        this.Desktop = (Desktop | 0);
        this.WideScreen = (WideScreen | 0);
    }
}

export function Breakpoints_$reflection() {
    return record_type("Utils.Breakpoints", [], Breakpoints, () => [["Mobile", int32_type], ["MobileLandscape", int32_type], ["Tablet", int32_type], ["Desktop", int32_type], ["WideScreen", int32_type]]);
}

export const BreakpointsModule_defaults = new Breakpoints(480, 600, 801, 1025, 1281);

export function useResponsive(breakpoints) {
    const breakpoints_1 = defaultArg(breakpoints, BreakpointsModule_defaults);
    const mx = HookImports_useMediaQuery(toText(printf("(max-width: %ipx)"))(breakpoints_1.MobileLandscape));
    const lx = HookImports_useMediaQuery(toText(printf("(max-width: %ipx)"))(breakpoints_1.Tablet));
    const tx = HookImports_useMediaQuery(toText(printf("(max-width: %ipx)"))(breakpoints_1.Desktop));
    const dx = HookImports_useMediaQuery(toText(printf("(max-width: %ipx)"))(breakpoints_1.WideScreen));
    if (mx) {
        return "mobile";
    }
    else if (lx) {
        return "mobileLandscape";
    }
    else if (tx) {
        return "tablet";
    }
    else if (dx) {
        return "desktop";
    }
    else {
        return "wideScreen";
    }
}

export function Links_Customer(groupId, id) {
    return RouterModule_encodeParts(ofArray(["group", int32ToString(groupId), "customer", int32ToString(id)]), 1);
}

export function Links_CustomerPath(groupId, customerId, page) {
    return ["group", int32ToString(groupId), "customer", int32ToString(customerId), page];
}

export function Links_GroupPath(groupId, page) {
    return ["group", int32ToString(groupId), page];
}

export function Links_Machine(groupId, customerId, id) {
    return RouterModule_encodeParts(ofArray(["group", int32ToString(groupId), "customer", int32ToString(customerId), "machine", int32ToString(id)]), 1);
}

export function Links_MachinePath(groupId, customerId, machineId, page) {
    return ["group", int32ToString(groupId), "customer", int32ToString(customerId), "machine", int32ToString(machineId), page];
}

export function Links_MachineActivities(groupId, customerId, machineId) {
    return RouterModule_encodeParts(ofArray(["group", int32ToString(groupId), "customer", int32ToString(customerId), "machine", int32ToString(machineId), "activity-plan"]), 1);
}

export function Links_Ticket(id) {
    return RouterModule_encodeParts(ofArray(["tickets", int32ToString(id)]), 1);
}

export function Links_Service(machineId, serviceId) {
    return RouterModule_encodeParts(ofArray(["machine", int32ToString(machineId), "service", int32ToString(serviceId)]), 1);
}

/**
 * Try create string of first and last name, if either is not defined, return the email instead
 */
export function Account_tryGetNameStr(user) {
    if (!isNullOrWhiteSpace(user.FirstName) && !isNullOrWhiteSpace(user.LastName)) {
        return `${user.FirstName} ${user.LastName}`;
    }
    else {
        return user.Email;
    }
}

